import axios, {
  AxiosError,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios';
import { JWT_ACCESS_TOKEN } from '../constants/jwt-tokens';
import cookie from './cookie';
import { LANGUAGES, FALLBACK_LNG, i18next_KEY } from './i18n';
import Swal from 'sweetalert2';
import { markSessionEvicted } from '../helpers/sessionEviction';

declare module 'axios' {
  export interface AxiosRequestConfig {
    /**
     * Opts a request out of the 401 sign-out handling below. Set only by the sign-out call,
     * which is made while the caller is already signing out and legitimately 401s once the
     * session is gone.
     */
    skipAuthRedirect?: boolean;
  }
}

/**
 * Error code the API returns with a 401 when the caller's session was ended by a sign-in on
 * another device. Mirrors SESSION_EVICTED in the backend.
 */
export const SESSION_EVICTED = 'SESSION_EVICTED';

/**
 * Where an unauthenticated caller is sent, and the one page that must not redirect to itself.
 */
const LOGIN_PATH = '/login';

/**
 * @description Reports whether a failed request opted out of the 401 sign-out handling
 *
 * @param {AxiosError} error - Axios error carrying the originating request config
 *
 * @returns {boolean} True when the request must not trigger a redirect or an eviction notice
 */
const skipsAuthRedirect = (error: AxiosError): boolean =>
  error?.config?.skipAuthRedirect === true;

/**
 * @description Determines whether a 401 was caused by the session being displaced by a sign-in
 * elsewhere, as opposed to an ordinary expired or missing token.
 *
 * @param {AxiosError} error - Axios error carrying the API response
 *
 * @returns {boolean} True when the API reported the session as evicted
 */
const isSessionEvicted = (error: AxiosError): boolean =>
  (error?.response?.data as { errorCode?: string })?.errorCode ===
  SESSION_EVICTED;

const baseUrl = import.meta.env.VITE_BASE_URL;

const axiosInstance = axios.create({
  baseURL: baseUrl,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
});

/**
 * @description On request interceptor that adds JWT token and language headers to all outgoing requests
 *
 * @SADtrace SAD-003-1
 *
 * @History
 * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
 * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
 *
 * @param {InternalAxiosRequestConfig<any>} config - Internal axios request config
 *
 * @returns {InternalAxiosRequestConfig<any>} Internal axios request config with added headers
 */
const onRequest = (
  config: InternalAxiosRequestConfig<any>,
): InternalAxiosRequestConfig<any> => {
  // Token will be dynamic so we can use any app-specific way to always
  // fetch the new token before making the call
  config.headers = config.headers ?? {};
  config.headers.Authorization = `Bearer ${cookie.get(JWT_ACCESS_TOKEN)}`;
  config.headers['x-dl-lang'] = Object.keys(LANGUAGES).includes(
    String(localStorage.getItem(i18next_KEY)),
  )
    ? String(localStorage.getItem(i18next_KEY))
    : FALLBACK_LNG;

  return config;
};

/**
 * @description On request error interceptor that handles errors during request setup
 *
 * @SADtrace SAD-003-1
 *
 * @History
 * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
 * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
 *
 * @param {AxiosError} error - Axios error
 *
 * @returns {Promise<AxiosError>} Promise rejection with axios error
 */
const onRequestError = (error: AxiosError): Promise<AxiosError> => {
  return Promise.reject(error);
};

/**
 * @description On response interceptor that handles successful API responses
 *
 * @SADtrace SAD-003-1
 *
 * @History
 * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
 * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
 *
 * @param {AxiosResponse} response - Axios response
 *
 * @returns {AxiosResponse} Axios response
 */
const onResponse = (response: AxiosResponse) => {
  return response;
};

/**
 * @description On response error interceptor that handles API errors, including 401 (unauthorized) and 429 (rate limit)
 *
 * @SADtrace SAD-003-1.1
 *
 * @History
 * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
 * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
 * 08-18-2026 aleksandar dukleski: DLA-1469 hand the eviction notice to the login page
 *
 * @param {AxiosError} error - Axios error
 *
 * @returns {Promise<AxiosError>} Promise rejection with axios error
 */
const onResponseError = (error: AxiosError): Promise<AxiosError> => {
  if (error?.response?.status === 401 && !skipsAuthRedirect(error)) {
    // A session displaced by a sign-in elsewhere (DLA-1469) is explained rather than silently
    // bounced, so the user learns their account was used on another device — the signal that
    // makes a credential compromise noticeable. The explanation is left for the login page to
    // render: this page is about to be destroyed by the redirect below, so a dialog shown here
    // races its own teardown, and reading i18n imperatively outside React bypasses the Suspense
    // boundary that guarantees translations have loaded.
    if (isSessionEvicted(error)) {
      markSessionEvicted();
    }

    // Every in-flight request 401s at once, and the login page calls the API itself, so an
    // unguarded assignment reloads /login repeatedly — each reload discarding the notice before
    // it can be read.
    if (window.location.pathname !== LOGIN_PATH) {
      window.location.href = LOGIN_PATH;
    }
  }

  if (error?.response?.status === 429) {
    Swal.fire({
      icon: 'warning',
      title: 'Too Many Requests',
      text: `Please wait 60 seconds before trying again.`,
      confirmButtonText: 'OK',
      confirmButtonColor: '#0895DF',
    });
  }

  return Promise.reject(error);
};

axiosInstance.interceptors.request.use(onRequest, onRequestError);
axiosInstance.interceptors.response.use(onResponse, onResponseError);

//added this custom paramsSerializer after upgrading axios in https://gitlab.com/amgis/cloud/drug-library-fe/-/merge_requests/833
//check github issue here: https:github.com/axios/axios/issues/5630#issuecomment-1859776798
axiosInstance.defaults.paramsSerializer = customParamsSerializer;

export const baseURL = baseUrl;
export default axiosInstance;

/**
 * @description Custom params serializer that handles arrays and objects for axios requests
 *
 * @SADtrace SAD-003-1
 *
 * @History
 * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
 * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
 *
 * @param {any} params - Query parameters object
 *
 * @returns {string} URL-encoded query string
 */
function customParamsSerializer(params: any): string {
  /**
   * @description Encodes a single query param value, handling dates and objects
   *
   * @SADtrace SAD-003-1
   *
   * @History
   * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
   * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
   *
   * @param {any} value - Value to encode
   *
   * @returns {string} URL-encoded string representation of value
   */
  function encodeAxiosParam(value: any): string {
    let v = value;
    if (toString.call(value) === '[object Date]') {
      v = value.toISOString();
    } else if (typeof value === 'object') {
      v = JSON.stringify(value);
    }
    return encodeURIComponent(v);
  }

  return Object.entries(params)
    .filter(
      ([, value]) =>
        value !== undefined &&
        value !== null &&
        (Array.isArray(value) ? value.length > 0 : true),
    )
    .map(([key, value]) => {
      if (Array.isArray(value)) {
        return value.map((v) => `${key}[]=${encodeAxiosParam(v)}`).join('&');
      }
      return `${key}=${encodeAxiosParam(value)}`;
    })
    .join('&');
}
